import React from "react";
import CodeMirror from "@uiw/react-codemirror";
import { html } from "@codemirror/lang-html";
import { css } from "@codemirror/lang-css";
import { EditorView } from "@codemirror/view";
import { Decoration, DecorationSet } from "@codemirror/view";
import { RangeSetBuilder } from "@codemirror/state";
import type { Extension } from "@codemirror/state";
import {
  syntaxHighlighting,
  defaultHighlightStyle,
} from "@codemirror/language";
import { oneDarkHighlightStyle } from "@codemirror/theme-one-dark";

export interface CodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  language?: "html" | "css" | "json";
  height?: string;
  placeholder?: string;
  readOnly?: boolean;
  theme?: "light" | "dark";
}

const CodeEditor: React.FC<CodeEditorProps> = ({
  value,
  onChange,
  language = "html",
  height = "400px",
  placeholder = "",
  readOnly = false,
  theme = "dark",
}) => {
  // Setup editor extensions based on language
  const getLanguageExtension = (): Extension => {
    switch (language) {
      case "html":
        return html();
      case "css":
        return css();
      case "json":
        // We intentionally don't depend on @codemirror/lang-json here.
        // JSON editing still works well with bracket matching + indentation.
        return [];
      default:
        return html();
    }
  };

  // VS Code-inspired theme (no external dependency required).
  // VS Code defaults:
  // - dark editor bg: #1e1e1e
  // - light editor bg: #ffffff
  // - selection (dark): #264f78
  // We keep this focused on "chrome" (background, gutter, selection, caret),
  // not full token colors.
  const vscodeDarkTheme = EditorView.theme(
    {
      "&": {
        height,
        fontSize: "14px",
        backgroundColor: "#1e1e1e",
        color: "#d4d4d4",
      },
      ".cm-editor": {
        backgroundColor: "#1e1e1e",
        color: "#d4d4d4",
      },
      ".cm-scroller": {
        backgroundColor: "#1e1e1e",
      },
      ".cm-content": {
        fontFamily:
          "ui-monospace, SFMono-Regular, SF Mono, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
        padding: "10px 0",
        caretColor: "#aeafad",
      },
      ".cm-line": {
        padding: "0 10px",
      },
      ".cm-gutters": {
        backgroundColor: "#1e1e1e",
        color: "#858585",
        border: "none",
      },
      ".cm-activeLine": {
        backgroundColor: "rgba(255, 255, 255, 0.04)",
      },
      ".cm-activeLineGutter": {
        backgroundColor: "rgba(255, 255, 255, 0.04)",
        color: "#c6c6c6",
      },
      ".cm-selectionBackground, ::selection": {
        backgroundColor: "#264f78 !important",
      },
      // Some global CSS can force selected text to become dark.
      // Explicitly keep selection foreground readable on the dark theme.
      ".cm-content ::selection": {
        color: "#ffffff",
      },
      ".cm-line ::selection": {
        color: "#ffffff",
      },
      ".cm-cursor": {
        borderLeftColor: "#aeafad",
      },
      ".cm-placeholder": {
        color: "#6a6a6a",
      },
    },
    { dark: true }
  );

  const vscodeLightTheme = EditorView.theme(
    {
      "&": {
        height,
        fontSize: "14px",
        backgroundColor: "#ffffff",
        color: "#1f1f1f",
      },
      ".cm-editor": {
        backgroundColor: "#ffffff",
        color: "#1f1f1f",
      },
      ".cm-scroller": {
        backgroundColor: "#ffffff",
      },
      ".cm-content": {
        fontFamily:
          "ui-monospace, SFMono-Regular, SF Mono, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
        padding: "10px 0",
        caretColor: "#000000",
      },
      ".cm-line": {
        padding: "0 10px",
      },
      ".cm-gutters": {
        backgroundColor: "#ffffff",
        color: "#6b6b6b",
        border: "none",
      },
      ".cm-activeLine": {
        backgroundColor: "rgba(0, 0, 0, 0.04)",
      },
      ".cm-activeLineGutter": {
        backgroundColor: "rgba(0, 0, 0, 0.04)",
        color: "#2b2b2b",
      },
      ".cm-selectionBackground, ::selection": {
        backgroundColor: "rgba(0, 120, 215, 0.25) !important", // VS Code-ish blue selection
      },
      ".cm-content ::selection": {
        color: "#000000",
      },
      ".cm-line ::selection": {
        color: "#000000",
      },
      ".cm-cursor": {
        borderLeftColor: "#000000",
      },
      ".cm-placeholder": {
        color: "#8a8a8a",
      },
    },
    { dark: false }
  );

  // Custom theme settings (layout/wrapping)
  const themeExtension = EditorView.theme({
    "&": {
      height,
      fontSize: "14px",
    },
    ".cm-content": {
      fontFamily: "monospace",
      padding: "10px 0",
    },
    ".cm-line": {
      padding: "0 10px",
    },
    ".cm-placeholder": {
      color: "#888",
    },
  });

  // Handlebars syntax highlighting
  const handlebarsTags = EditorView.decorations.of(
    (view: EditorView): DecorationSet => {
      const builder = new RangeSetBuilder<Decoration>();
      const content = view.state.doc.toString();
      const pattern = /{{[^}]+}}/g;
      let match;

      while ((match = pattern.exec(content)) !== null) {
        const start = match.index;
        const end = start + match[0].length;
        builder.add(
          start,
          end,
          Decoration.mark({ class: "handlebars-expression" })
        );
      }

      return builder.finish();
    }
  );

  // Combine all extensions
  const extensions: Extension[] = [
    getLanguageExtension(),
    theme === "dark" ? vscodeDarkTheme : vscodeLightTheme,
    themeExtension,
    handlebarsTags,
    // Syntax/token colors:
    // - dark: use One Dark highlight style for readable tokens on dark bg
    // - light: use CodeMirror default highlight style
    theme === "dark"
      ? syntaxHighlighting(oneDarkHighlightStyle)
      : syntaxHighlighting(defaultHighlightStyle),
    EditorView.lineWrapping,
    EditorView.editable.of(!readOnly),
  ];

  return (
    <div
      className="border rounded-md overflow-hidden"
      style={{
        backgroundColor: theme === "dark" ? "#1e1e1e" : "#ffffff",
      }}
    >
      <style>{`
        .handlebars-expression {
          color: #c678dd;
          font-weight: bold;
        }
        .cm-editor {
          height: ${height};
        }
        .cm-focused {
          outline: none !important;
        }
        /* Keep selection readable */
        .cm-selectionMatch {
          background-color: rgba(38, 79, 120, 0.35) !important;
        }
        .cm-matchingBracket {
          background-color: rgba(56, 139, 253, 0.25) !important;
          outline: 1px solid rgba(56, 139, 253, 0.35);
        }
      `}</style>
      <CodeMirror
        value={value}
        onChange={onChange}
        extensions={extensions}
        placeholder={placeholder}
        basicSetup={{
          lineNumbers: true,
          highlightActiveLineGutter: true,
          highlightSpecialChars: true,
          history: true,
          foldGutter: true,
          drawSelection: true,
          dropCursor: true,
          allowMultipleSelections: true,
          indentOnInput: true,
          syntaxHighlighting: true,
          bracketMatching: true,
          closeBrackets: true,
          autocompletion: true,
          rectangularSelection: true,
          crosshairCursor: true,
          highlightActiveLine: true,
          highlightSelectionMatches: true,
          closeBracketsKeymap: true,
          defaultKeymap: true,
          searchKeymap: true,
          historyKeymap: true,
          foldKeymap: true,
          completionKeymap: true,
          lintKeymap: true,
        }}
      />
    </div>
  );
};

export default CodeEditor;
